/** שגיאת API טיפוסית — כוללת קוד HTTP וגוף תשובה מפוענח כשניתן */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** מזהה חוסר קישוריות (offline / timeout / DNS) לעומת שגיאת שרת אמיתית */
export class NetworkError extends Error {
  constructor(message = 'אין חיבור לשרת') {
    super(message);
    this.name = 'NetworkError';
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { timeoutMs?: number },
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), init?.timeoutMs ?? 15_000);
  try {
    const res = await fetch(path, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      signal: controller.signal,
      ...init,
    });
    const text = await res.text();
    const body: unknown = text ? safeJson(text) : null;
    if (!res.ok) {
      const message =
        (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
          ? body.error
          : null) ?? `שגיאת שרת (${res.status})`;
      throw new ApiError(message, res.status, body);
    }
    return body as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new NetworkError('הבקשה חרגה מזמן ההמתנה');
    }
    throw new NetworkError();
  } finally {
    clearTimeout(timeout);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) => request<T>(path, { method: 'GET', timeoutMs }),
  post: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body), timeoutMs }),
  put: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body), timeoutMs }),
  delete: <T>(path: string, body?: unknown, timeoutMs?: number) =>
    request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body), timeoutMs }),
};
